import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { useOrdersetSettingsPageBusinessLogic } from '../../../../../../../logic/context'
import { InfiniteScrollList } from '@labsavvyapp/ui-components'
import { CheckboxGroup, fetchNext, hasMore, LoadingIcon, Select, TextInput } from '@labsavvyapp/components'
import { useQueryListCompendiumPanelsApi } from '../../../../../../../../../../viewmodels'
import { OrdersetCategoryTestItem } from '../OrdersetCategoryTestItem'
import { OrdersetCategoryTestSelectionControls } from './components'
import { calculateOrdersetClientPrice, calculateOrdersetPatientPrice } from '../../../../../../../utils'
import { CompendiumPanelWithType } from '../../../../../../../types'

const StyledScrollArea = styled.div`
  display: flex;
  flex-flow: column nowrap;
  background-color: white;
  border-radius: 4px;
  border: 1px solid #e5e5e5;
  padding: 10px;
  width: 100%;
  height: 500px;
`

const ListHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr repeat(2, 65px) 20px;
  grid-template-rows: 1fr;
  grid-column-gap: 16px;
  grid-row-gap: 0px;
  margin-top: 10px;
  font-size: 12px;
  border-bottom: 1px solid #e9ecef;
`

const NothingMessage = styled.div`
  display: flex;
  align-items: center;
  align-content: center;
  justify-content: center;
  width: 100%;
  color: gray;
`

const LoadingMessage = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 5px;
  align-content: center;
  align-items: center;
`

const ControlsSection = styled.section`
  width: 100%;
  margin-bottom: 5px;
`

const TestSelectionAndViewingSection = styled.section`
  display: grid;
  grid-template-columns: 1fr 50px 1fr;
  grid-template-rows: 1fr;
  grid-column-gap: 10px;
  grid-row-gap: 0px;
  width: 100%;
`

interface OrdersetCategoryTestSelectionProps {
  selectedTests: CompendiumPanelWithType[]
  handleSelectedTestsChange: React.Dispatch<React.SetStateAction<CompendiumPanelWithType[]>>
  handleSaveEditOrdersetCategoryClick: () => void
}
export const OrdersetCategoryTestSelection = (props: OrdersetCategoryTestSelectionProps) => {
  const { selectedTests, handleSelectedTestsChange } = props

  const pageLogic = useOrdersetSettingsPageBusinessLogic()
  const userSelectedLaboratoryId = pageLogic?.userSelectedLaboratoryId ?? null

  const [groupSearchKey, setGroupSearchKey] = useState<string>('')
  const [selectedTestSearchKey, setSelectedTestSearchKey] = useState<string>('')

  const [selectedGroupTestsForSelection, setSelectedGroupTestsForSelection] = useState<string[]>([])
  const [selectedIndividualTestsForSelection, setSelectedIndividualTestsForSelection] = useState<string[]>([])
  const [selectedTestsForUnselection, setSelectedTestsForUnselection] = useState<string[]>([])

  const isSelectButtonDisabled = [...selectedGroupTestsForSelection, ...selectedIndividualTestsForSelection]?.length <= 0
  const isUnselectTestButtonDisabled = selectedTestsForUnselection?.length <= 0

  useEffect(() => {
    const clientPackagePrice = calculateOrdersetClientPrice(selectedTests)
    const retailPackagePrice = calculateOrdersetPatientPrice(selectedTests)
    pageLogic?.handleTotalCreateOrdersetPatientClientChange(Number(clientPackagePrice))
    pageLogic?.handleTotalCreateOrdersetPatientPriceChange(Number(retailPackagePrice))
  }, [selectedTests])

  const {
    data: groupTests,
    loading: groupTestsLoading,
    fetchMore: fetchMoreGroupTests,
  } = useQueryListCompendiumPanelsApi({
    providerId: userSelectedLaboratoryId,
    type: 'any',
    search: groupSearchKey,
  })

  const tests: CompendiumPanelWithType[] =
    groupTests?.compendium_panels
      .filter(
        (item) =>
          item?.name?.toLowerCase().includes(groupSearchKey?.toString()?.toLowerCase() ?? '') ||
          item?.code?.toLowerCase().includes(groupSearchKey?.toString()?.toLowerCase() ?? ''),
      )
      ?.map((test) => ({
        ...test,
        type: 'group',
      })) ?? []

  const seen = new Set()
  const filteredGroupTests = tests.filter((item) => {
    const duplicate = seen.has(item.code)
    seen.add(item.code)
    return !duplicate
  })

  let groupPagination = {
    page: 0,
    pages: 0,
    total: 0,
  }

  if (groupTests) {
    groupPagination.page = groupTests.page
    groupPagination.pages = groupTests.pages
    groupPagination.total = groupTests.total
  }

  const selectedGroupTestsCodes = selectedTests.map((test) => {
    return test.code
  })

  const handleTestSelection = () => {
    const updatedSelectedGroupTests = filteredGroupTests.filter((test) =>
      selectedGroupTestsForSelection.some((selectedTestId) => test.code === selectedTestId),
    )

    const seen = new Set()
    const uniqueTests = [...selectedTests, ...updatedSelectedGroupTests].filter((item) => {
      const duplicate = seen.has(item.code)
      seen.add(item.code)
      return !duplicate
    })

    handleSelectedTestsChange(uniqueTests)

    setSelectedGroupTestsForSelection([])
    setSelectedIndividualTestsForSelection([])
  }

  const handleTestUnselection = () => {
    const updatedGroupTests = selectedTests.filter(
      (test) => !selectedTestsForUnselection.some((selectedTestId) => test.code === selectedTestId && test.type === 'group'),
    )

    handleSelectedTestsChange(updatedGroupTests)
    setSelectedTestsForUnselection([])
  }

  return (
    <>
      <TestSelectionAndViewingSection>
        <StyledScrollArea>
          <ControlsSection>
            <TextInput
              placeholder="Search by code or name..."
              value={groupSearchKey}
              onChange={(event) => {
                setGroupSearchKey(event.currentTarget.value)
              }}
            />
            <ListHeader>
              <p></p>
              <p>Client Bill</p>
              <p>Patient Bill</p>
              <p></p>
            </ListHeader>
          </ControlsSection>
          <InfiniteScrollList
            dataLength={filteredGroupTests?.length || 0}
            scrollableTarget="group-test-selection-list"
            hasMore={hasMore(groupPagination)}
            next={() =>
              fetchNext('listCompendiumPanels', 'compendium_panels', {
                page: groupPagination.page,
                fetchMore: fetchMoreGroupTests,
                filter: {
                  search: '',
                  providerId: userSelectedLaboratoryId,
                },
              })
            }
            saveScrollPosition={false}
          >
            {!groupTestsLoading && !filteredGroupTests.length ? (
              <NothingMessage>Nothing here...</NothingMessage>
            ) : (
              <CheckboxGroup.Group value={selectedGroupTestsForSelection} onChange={setSelectedGroupTestsForSelection}>
                {filteredGroupTests?.map((data) => (
                  <OrdersetCategoryTestItem key={data?.code} data={data} isByGroups={true} selectedCodes={selectedGroupTestsCodes} />
                ))}
              </CheckboxGroup.Group>
            )}

            {groupTestsLoading && (
              <LoadingMessage>
                <LoadingIcon size="sm" color="gray" />
                Loading more items
              </LoadingMessage>
            )}
          </InfiniteScrollList>
        </StyledScrollArea>

        <OrdersetCategoryTestSelectionControls
          isSelectButtonDisabled={isSelectButtonDisabled}
          handleTestSelection={handleTestSelection}
          isUnselectTestButtonDisabled={isUnselectTestButtonDisabled}
          handleTestUnselection={handleTestUnselection}
        />

        <StyledScrollArea>
          <TextInput
            placeholder="Search by code or name..."
            value={selectedTestSearchKey}
            onChange={(event) => {
              setSelectedTestSearchKey(event.currentTarget.value)
            }}
          />
          <ListHeader>
            <p></p>
            <p>Client Bill</p>
            <p>Patient Bill</p>
            <p></p>
          </ListHeader>
          <InfiniteScrollList dataLength={[...selectedTests].length || 0}>
            <CheckboxGroup.Group value={selectedTestsForUnselection} onChange={setSelectedTestsForUnselection}>
              {selectedTests
                .filter(
                  (item) =>
                    item?.name?.toLowerCase().includes(selectedTestSearchKey?.toString()?.toLowerCase() ?? '') ||
                    item?.code?.toLowerCase().includes(selectedTestSearchKey?.toString()?.toLowerCase() ?? ''),
                )
                .map((selectedTest) => (
                  <OrdersetCategoryTestItem key={selectedTest.code} data={selectedTest} isByGroups={true} selectedCodes={[]} />
                ))}
            </CheckboxGroup.Group>
          </InfiniteScrollList>
        </StyledScrollArea>
      </TestSelectionAndViewingSection>
    </>
  )
}
